import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import reportScheduleService from '@api/v1/reportScheduleService'
import useReadState from '@hooks/useReadState'
import { SCHEDULES_QUERY_KEY } from './schedulesQueryKey'

/**
 * The one definition of "the owner's schedules" (fixes the stale-list defect).
 *
 * There are TWO consumers of this data — the Manage Schedules page and the Queue
 * page's `ManageSchedulesLink` count chip (spec 074) — and they were each
 * declaring their own `useQuery` with their own `staleTime`. That is how the list
 * and the count came to disagree, so both now go through here and share a key,
 * a fetcher and a freshness policy by construction rather than by convention.
 *
 * ## What was actually broken
 *
 * Creating a schedule invalidated nothing. All three create call sites
 * (`ReportQueue.jsx`, `ReportView.jsx`, `SpecializedFilterBar.jsx`) awaited
 * `reportScheduleService.create()` and moved on. Combined with the app-wide
 * `staleTime: 5 * 60 * 1000` in `main.jsx:21` and `refetchOnWindowFocus: false`,
 * that produced the reported symptom exactly:
 *
 *   1. The Queue page mounts and populates `['report-schedules']`.
 *   2. You create a schedule. Nothing invalidates the key.
 *   3. You click through to Manage Schedules. It subscribes to the SAME key,
 *      finds data less than five minutes old, and therefore does NOT refetch —
 *      so it renders the list from *before* the create.
 *   4. A hard refresh drops the in-memory cache, so the list is suddenly right.
 *
 * Two defences, deliberately both:
 *
 *  - `useInvalidateSchedules()` at every mutation, which is the actual fix; and
 *  - `refetchOnMount: 'always'`, because this page's entire job is telling you
 *    what will run while you are not watching. One cheap D1 read per navigation
 *    is a fair price for never showing a stale answer to that question, and it
 *    also covers changes we did not make ourselves (the cron firing a schedule
 *    and moving its "last run", or another tab).
 */

/**
 * The cache key every consumer must use.
 *
 * It lives OUTSIDE `reportScheduleService` deliberately: nearly every suite that
 * touches this feature mocks the service module wholesale, and a key imported from a
 * mocked module comes back `undefined`, which makes TanStack throw on a queryKey.
 * Spec 152 moved the definition one step further, into `schedulesQueryKey.js`, for the
 * same reason — suites mock THIS module too, and `useResultMaxAgeDays` needs the key
 * under them. Re-exported here, so every existing import is unchanged.
 */
export { SCHEDULES_QUERY_KEY }

/** Read the flag at call time, not module scope, so suites can stub it. */
const jobsEnabled = () => import.meta.env.VITE_REPORT_JOBS === 'true'

/**
 * @param {object} [opts]
 * @param {boolean} [opts.enabled]
 * @param {'mine'|'group'|'firm'} [opts.scope] visibility scope (CC-50282)
 *
 * The scope is part of the CACHE KEY, so switching Mine → My group is a separate
 * cache entry rather than a mutation of the same one; `SCHEDULES_QUERY_KEY` is the
 * prefix, so one `invalidateQueries` still clears every scope at once.
 *
 * Returns the query PLUS flattened `schedules` / `scope` / `scopeResolved`, because
 * the endpoint returns an envelope and `scopeResolved: false` has to reach the UI —
 * it means the CB group lookup failed and the server narrowed to "mine", which the
 * page must state rather than render as an empty group.
 *
 * ...and `state` / `ready`, which are the ONLY way a consumer can tell an empty
 * schedule list from one that was never read. See the note on them below.
 */
export function useSchedulesQuery({ enabled = true, scope = 'mine' } = {}) {
  const query = useQuery({
    queryKey: [...SCHEDULES_QUERY_KEY, scope],
    queryFn: () => reportScheduleService.list(scope),
    enabled: enabled && jobsEnabled(),
    staleTime: 30_000,
    refetchOnMount: 'always',
  })

  /*
   * A read has THREE outcomes, not two (CC-53132, `@api/v1/readState`).
   *
   * `isLoading` — which both consumers destructure — is `isPending && isFetching`,
   * so it is FALSE for a query that is pending but not in flight. Above all when
   * react-query PAUSES the fetch: with the default `networkMode: 'online'` it
   * refuses to fire while `onlineManager` believes the browser is offline, so
   * `queryFn` is never called, nothing throws, nothing is logged and `data` stays
   * undefined. The page then falls straight past its loading branch and its error
   * branch into "You don't have any schedules yet." — which is the one sentence
   * that will send a user off to create a duplicate of a schedule that already
   * exists and is still going to fire.
   *
   * `error` was already surfaced (the page has a proper failed-read panel), so
   * blocked/idle are the states nothing downstream could see. They can now:
   *
   *   ready   → the list is the list, and `schedules.length === 0` may be reported
   *             as "no schedules" out loud.
   *   blocked → say the server is unreachable, say it is NOT an empty list, offer
   *             `refetch`. Never a count.
   *   idle    → `VITE_REPORT_JOBS` is off (or the caller passed `enabled: false`).
   *             Deliberately NOT folded into `loading`: both consumers already
   *             render their own flag-off panel ahead of the list, and folding it
   *             in would turn any consumer that forgot that gate into a permanent
   *             spinner.
   */
  const state = useReadState(`report-schedules:${scope}`, query)
  const ready = state === 'ready'

  return {
    ...query,
    state,
    ready,
    /*
     * Still coerced to an array, deliberately — this is NOT the fix, `ready` is.
     *
     * Both consumers dereference this at the top of the component body
     * (`schedules.filter(...)` for their count chips) before any render branch
     * runs, so handing them `undefined` would not surface the failed read, it
     * would throw a TypeError and white-screen the page. A blank page is not a
     * more honest answer than a wrong one. The empty array is safe precisely
     * because it is no longer the only thing they get: every count and every
     * empty state must be gated on `ready`.
     */
    schedules: query.data?.schedules || [],
    scope: query.data?.scope || scope,
    /*
     * Only a completed read can say anything about group scope. Before, `data`
     * being undefined made this `true` — an unresolved read positively asserting
     * "we resolved your audience". `true` is still what an unresolved read
     * returns, because the alternative (`false`) renders "showing your own
     * schedules only", which is a *worse* false statement when no list is being
     * shown at all. Gating it here makes that a decision rather than an accident:
     * on a non-ready read the page owes the user the `state` panel, not this
     * warning.
     */
    scopeResolved: ready ? query.data?.scopeResolved !== false : true,
    // Spec 169 — the server's answer for THIS read: it lists colleagues' private
    // schedules because CB said the reader is an administrator. Only a completed read can
    // say so; anything else is false (never inferred from a token claim).
    adminView: ready && query.data?.adminView === true,
  }
}

/**
 * Invalidate both consumers. Call after EVERY schedule mutation — create, update,
 * pause/resume, skip, run-now and delete all change what this list should say.
 */
export function useInvalidateSchedules() {
  const qc = useQueryClient()
  return useCallback(() => qc.invalidateQueries({ queryKey: SCHEDULES_QUERY_KEY }), [qc])
}
